import { CSSProperties, ReactNode, useState } from "react";
import { ExternalLink, Mail, Pencil, Phone } from "lucide-react";
import toast from "react-hot-toast";
import { Order } from "../../SalesPage/SalesPageInterface";
import { CustomerInterface, ProductInterface } from "../OrderDetailsInterface";
import { ShippingAddressInterface } from "../../../components/cards/ShippingAddressInterface";
import { ModalWrapper } from "../../../components/modals/ModalWrapper";
import { dictionary } from "../../../utils/dictionary";
import { useAuth } from "../../../context/AuthContext";
import { formatDateInZone } from "../../../utils/formatters";
import { updateShippingAddress } from "../../../services/whatsapp/whatsappService";
import { handleApi } from "../../../utils/handleApi";
import { decodeTokenService } from "../../../services/decodeTokenService";
import { PaidState } from "../../../types/order/paidState";
import { getOrderTotals } from "./orderTotals";
import { StatusDot } from "./StatusDot";

const FIELD_LABELS: Record<string, string> = {
  country: "País",
  country_code: "País",
  shopify_order_id: "ID de orden Shopify",
  shopify_order_name: "Pedido Shopify",
  shopify_financial_status: "Estado de pago Shopify",
  shopify_fulfillment_status: "Estado de entrega Shopify",
  shopify_checkout_token: "Token de checkout Shopify",
};

const normalizeKey = (key: unknown) => String(key ?? "").trim().toLowerCase();

const formatLabel = (key: unknown) => {
  const normalizedKey = normalizeKey(key);
  if (FIELD_LABELS[normalizedKey]) return FIELD_LABELS[normalizedKey];

  return String(key ?? "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const formatValue = (value: unknown) => {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return JSON.stringify(value);
};

const GroupLabel = ({ children, action }: { children: ReactNode; action?: ReactNode }) => (
  <div className="mb-2 flex items-center justify-between">
    <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{children}</p>
    {action}
  </div>
);

const Property = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="py-1">
    <p className="text-xs leading-4 text-gray-400">{label}</p>
    <div className="text-sm font-medium leading-5 text-gray-900">{children}</div>
  </div>
);

const getOrderChannelLabel = (order: Order) => {
  const channel = String(order.channel || "").trim().toLowerCase();
  const source = String(order.tracking_source || "").trim().toLowerCase();
  if (channel === "chatbot" || source === "chatbot_valeria") return "Chatbot";
  if (channel) return order.channel;
  return "Embudo";
};

export const OrderPropertiesPanel = ({
  order,
  customer,
  products,
  shippingAddress,
  zipCode,
  confirmationId,
  aiEnabled,
  onAddressUpdated,
  className = "",
  style,
}: {
  order: Order;
  customer?: CustomerInterface;
  products: ProductInterface[];
  shippingAddress?: ShippingAddressInterface;
  zipCode?: string;
  confirmationId?: number;
  aiEnabled?: boolean;
  onAddressUpdated: () => void;
  className?: string;
  style?: CSSProperties;
}) => {
  const { language, timezone } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    address: shippingAddress?.address || "",
    address_detail: shippingAddress?.address_detail || "",
    city: shippingAddress?.city || "",
    department: shippingAddress?.department || "",
  });

  const { total, currencyCode, formatAmount } = getOrderTotals(order, products);
  const isPaid = order.payment_state === PaidState.PAID;
  const paymentStatusLabel = isPaid
    ? dictionary["paid-state"][language]
    : "Por recaudar";
  const paymentMethodLabel =
    order.payment_method === "cod"
      ? dictionary["counter-delivery"][language]
      : order.payment_method || "";
  const channelLabel = getOrderChannelLabel(order);

  const fullName = customer
    ? `${customer.name || ""} ${customer.last_name || ""}`.trim()
    : "";
  const phone = customer
    ? `${customer.prefix_phone || ""} ${customer.phone || ""}`.trim()
    : "";

  const customFields = (customer?.custom_fields ?? [])
    .filter((field) => !["zip", "zip code", "postal_code", "postal code", "codigo postal", "código postal"].includes(normalizeKey(field?.key)))
    .map((field) => ({
      key: field?.key,
      label: formatLabel(field?.key),
      value: formatValue(field?.value),
    }))
    .filter((field) => field.key && field.value);

  const builderBaseUrl = import.meta.env.VITE_REACT_APP_URL_BUILDER as string | undefined;
  const canOpenFunnel = Boolean(builderBaseUrl && order.website_id);

  const handleOpenFunnel = () => {
    if (!canOpenFunnel) return;
    const userId = decodeTokenService.getUserIdFromToken() || "";
    const token = localStorage.getItem("access_token") || "";
    window.open(
      `${builderBaseUrl}builder/funnel/${userId}/${order.website_id}?token=${token}`,
      "_blank"
    );
  };

  const handleEdit = () => {
    if (aiEnabled) {
      toast.error(dictionary["ai-active-warning"]?.[language]);
      return;
    }
    setForm({
      address: shippingAddress?.address || "",
      address_detail: shippingAddress?.address_detail || "",
      city: shippingAddress?.city || "",
      department: shippingAddress?.department || "",
    });
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!confirmationId) return;
    setSaving(true);
    const { error } = await handleApi(
      updateShippingAddress(confirmationId, {
        address: form.address,
        addressDetail: form.address_detail,
        city: form.city,
        department: form.department,
      })
    );
    if (!error) {
      toast.success(dictionary["address-updated"]?.[language]);
      onAddressUpdated();
      setIsEditing(false);
    }
    setSaving(false);
  };

  return (
    <>
      <div
        className={`rounded-2xl border border-gray-100 bg-white p-5 shadow-[0_2px_12px_rgba(112,144,176,0.08)] ${className}`}
        style={style}
      >
        <GroupLabel>{dictionary["payment-section-label"][language]}</GroupLabel>
        <p className="text-xl font-bold leading-6 text-gray-900 tabular-nums">
          {formatAmount(total)}{" "}
          <span className="text-xs font-normal text-gray-400">{currencyCode}</span>
        </p>
        <p className="mt-1.5 flex flex-wrap items-center gap-x-2 text-xs text-gray-500">
          <StatusDot
            tone={isPaid ? "success" : "neutral"}
            label={paymentStatusLabel}
          />
          {paymentMethodLabel && <span>· {paymentMethodLabel}</span>}
        </p>
        {isPaid && (
          <p className="mt-1 text-xs text-gray-400">
            {dictionary["paid-on"][language]} {formatDateInZone(order.created_at, timezone)}
          </p>
        )}

        <div className="mt-4 grid grid-cols-1 gap-x-4 border-t border-gray-100 pt-3 sm:grid-cols-2 lg:grid-cols-2">
          <div>
            <GroupLabel>{dictionary["customer"][language]}</GroupLabel>
            <p className="text-sm font-medium leading-5 text-gray-900">
              {fullName || customer?.email || "—"}
            </p>
            <p className="text-xs text-gray-400">{dictionary["first-time-customer"][language]}</p>
            <div className="mt-1.5 space-y-1">
              {customer?.email && (
                <p className="flex min-w-0 items-start gap-1.5 text-xs leading-4 text-gray-600">
                  <Mail className="mt-px h-3.5 w-3.5 shrink-0 text-gray-400" />
                  <span className="min-w-0 break-all">{customer.email}</span>
                </p>
              )}
              {phone && (
                <p className="flex items-center gap-1.5 text-xs leading-4 text-gray-600">
                  <Phone className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                  {phone}
                </p>
              )}
            </div>
          </div>

          <div className="mt-4 sm:mt-0">
            <GroupLabel
              action={
                confirmationId ? (
                  <button
                    type="button"
                    onClick={handleEdit}
                    title={dictionary["edit-address"]?.[language]}
                    className="-m-1 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
                  >
                    <Pencil size={13} />
                  </button>
                ) : undefined
              }
            >
              {dictionary["shipping-address"][language]}
            </GroupLabel>
            {shippingAddress ? (
              <div className="space-y-0.5 text-xs leading-5 text-gray-600">
                <p className="text-sm font-medium leading-5 text-gray-900">
                  {shippingAddress.name}
                  {shippingAddress.last_name && ` ${shippingAddress.last_name}`}
                </p>
                <p className="break-words">{shippingAddress.address}</p>
                {shippingAddress.address_detail && (
                  <p className="break-words">{shippingAddress.address_detail}</p>
                )}
                {(shippingAddress.city || shippingAddress.department) && (
                  <p>
                    {shippingAddress.city}
                    {shippingAddress.city && shippingAddress.department && " – "}
                    {shippingAddress.department}
                  </p>
                )}
                {shippingAddress.neighborhood && <p>{shippingAddress.neighborhood}</p>}
                {zipCode && (
                  <p className="text-gray-400">
                    {dictionary["zip-code-label"][language]} {zipCode}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-xs text-gray-400">—</p>
            )}
          </div>
        </div>

        <div
          className={`mt-4 grid grid-cols-1 gap-x-4 border-t border-gray-100 pt-3 ${
            customFields.length > 0 ? "sm:grid-cols-2 lg:grid-cols-2" : ""
          }`}
        >
          {customFields.length > 0 && (
            <div>
              <GroupLabel>{dictionary["additional-information"][language]}</GroupLabel>
              <div className="space-y-1">
                {customFields.map((field, index) => {
                  const isLongValue = field.value.length > 32 || field.value.includes("gid://");
                  return (
                    <div key={`${field.key}-${index}`} className="py-0.5">
                      <p className="text-xs leading-4 text-gray-400">{field.label}</p>
                      <p
                        className={`text-sm font-medium leading-5 text-gray-900 ${
                          isLongValue ? "break-all font-mono text-xs leading-4" : "break-words"
                        }`}
                        title={field.value}
                      >
                        {field.value}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className={customFields.length > 0 ? "mt-4 sm:mt-0" : ""}>
            <GroupLabel>{dictionary["order-section-label"][language]}</GroupLabel>
            <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
              <Property label={dictionary["order-created-label"][language]}>
                {formatDateInZone(order.created_at, timezone)}
              </Property>
              {canOpenFunnel ? (
                <Property label={dictionary["order-funnel-label"][language]}>
                  <button
                    type="button"
                    onClick={handleOpenFunnel}
                    className="inline-flex items-center gap-1 text-sm font-semibold text-fluxi transition-colors hover:text-fluxi-dark"
                  >
                    {dictionary["open-funnel"][language]}
                    <ExternalLink className="h-3.5 w-3.5" />
                  </button>
                </Property>
              ) : order.channel ? (
                <Property label={dictionary["order-channel-label"][language]}>
                  {channelLabel}
                </Property>
              ) : null}
            </div>
            {canOpenFunnel && channelLabel && (
              <Property label={dictionary["order-channel-label"][language]}>
                {channelLabel}
              </Property>
            )}
          </div>
        </div>
      </div>

      {isEditing && (
        <ModalWrapper isOpen onClose={() => setIsEditing(false)}>
          <div className="max-h-[85vh] overflow-y-auto p-6">
            <h3 className="text-lg font-semibold mb-4 pr-8">
              {dictionary["edit-address"]?.[language]}
            </h3>
            <div className="space-y-3">
              {(
                [
                  ["address", dictionary["shipping-address"]?.[language]],
                  ["address_detail", dictionary["address-detail"]?.[language]],
                  ["city", dictionary["city"]?.[language]],
                  ["department", dictionary["department"]?.[language]],
                ] as const
              ).map(([field, label]) => (
                <div key={field}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                  <input
                    type="text"
                    value={form[field]}
                    onChange={(event) => setForm({ ...form, [field]: event.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setIsEditing(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                {dictionary["cancel"]?.[language]}
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-fluxi text-white hover:bg-fluxi-dark disabled:opacity-50"
              >
                {saving ? "..." : dictionary["save"]?.[language]}
              </button>
            </div>
          </div>
        </ModalWrapper>
      )}
    </>
  );
};
